import React, { useState } from 'react';

import {
  ExclamationCircleOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button, Flex, Form, Input, Modal, Typography } from 'antd';
import { FormikProvider, useFormik } from 'formik';
import { useParams } from 'react-router-dom';

import { useUpdatePartnerClosureRequest } from '../../api/updatePartnerClosureRequest';
import { ClosureRequest } from '../../types';
import { closureRequestStatuses } from '../../types';
import { SuccessfullClosureApprovedToast } from '../SuccessfullClosureApprovedToast';
import ScopedCta from '@/components/ScopedCta';
import { scopes } from '@/config';
import useError from '@/hooks/useError';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

const { Text } = Typography;

export type FormData = {
  confirmPartnerId?: string;
};

const validateForm = (partnerId) => (values) => {
  const errors: Partial<FormData> = {};
  if (!values.confirmPartnerId || values.confirmPartnerId !== partnerId) {
    errors.confirmPartnerId = 'Please provide the correct Partnership ID';
  }

  return errors;
};

export const CloseThisOrganisation = ({
  closureRequest,
  isLoadingPartnerStats,
  isErrorPartnerStats,
  closePartnerButtonDisable,
}: {
  closureRequest: ClosureRequest | undefined | null;
  isLoadingPartnerStats: boolean;
  isErrorPartnerStats: boolean;
  closePartnerButtonDisable: boolean;
}) => {
  const { id: partnerId } = useParams() as { id: string };

  const addError = useError();
  const addSuccess = useToasts((state) => state.addItem);

  const [open, setOpen] = useState(false);

  const { mutateAsync: updatePartnerClosureRequest, isPending: isLoading } =
    useUpdatePartnerClosureRequest();

  const onSubmit = async () => {
    const isValid = await formik.validateForm();

    if (!isValid) {
      return;
    }

    try {
      // Patch the partner closure request with the status : "VALIDATED"
      await updatePartnerClosureRequest({
        ...closureRequest!,
        status: closureRequestStatuses.Approved,
      });
      addSuccess(
        {
          msg: <SuccessfullClosureApprovedToast />,
          type: MessageType.Success,
        },
        'root',
        {
          duration: 7500,
        }
      );
    } catch (error) {
      addError(
        'Error while closing this organization, please retry or contact support'
      );
    }

    handleCancel();
  };

  const formik = useFormik<FormData>({
    initialValues: {},
    validate: validateForm(partnerId),
    onSubmit,
  });

  const showModal = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
    // Clear formik state
    formik.resetForm();
  };

  const handleOk = () => {
    formik.handleSubmit();
  };

  return (
    <>
      <ScopedCta
        component={Button}
        type="primary"
        danger
        disabled={closePartnerButtonDisable}
        onClick={showModal}
        data-testid="delete-partner"
        requiredScopes={[scopes.MANAGE_LPVILLAGE_PARTNER]}
      >
        {isLoadingPartnerStats && <LoadingOutlined />}
        {isErrorPartnerStats && <WarningOutlined />}
        Close this organisation
      </ScopedCta>
      <Modal
        destroyOnClose
        open={open}
        onOk={handleOk}
        confirmLoading={formik.isSubmitting}
        okText="Close this organisation"
        onCancel={handleCancel}
        okButtonProps={{
          'data-testid': 'ok-button',
          danger: true,
          variant: 'outlined',
          disabled:
            closePartnerButtonDisable || isLoading || formik.isSubmitting,
        }}
        width={{
          xs: '90%',
          sm: '80%',
          md: '70%',
          lg: '60%',
          xl: '41.25rem',
          xxl: '41.25rem',
        }}
        styles={{
          body: {
            paddingBottom: '2rem',
          },
          footer: {
            marginTop: '2rem',
          },
        }}
      >
        <Flex
          gap="1.5rem"
          vertical
          style={{
            background: 'transparent',
          }}
        >
          {/* Icon + confirm phrase */}
          <Flex gap="0.75rem" vertical align="center">
            <ExclamationCircleOutlined
              style={{
                fontSize: '3.625rem',
                color: '#C82F21',
                padding: '.4375rem',
              }}
            />

            <Text
              style={{
                fontSize: '1.75rem',
                fontWeight: '500',
                textAlign: 'center',
              }}
            >
              Are you sure you want to close this organisation ?
            </Text>
          </Flex>
          {/* Partnership Name + Content + Reason + FAQ */}
          <Text>User will no more have access to the WebApp.</Text>
          <FormikProvider value={formik}>
            <Form component="form" onFinish={formik.handleSubmit}>
              <Form.Item
                layout="vertical"
                name="confirmPartnerId"
                label="Please enter the Partnership ID to confirm the closure"
                style={{ width: '100%' }}
                validateStatus={
                  formik.errors.confirmPartnerId ? 'error' : undefined
                }
                help={formik.errors.confirmPartnerId}
              >
                <Input
                  status={formik.errors.confirmPartnerId ? 'error' : undefined}
                  disabled={formik.isSubmitting}
                  placeholder="Please enter the Partnership ID to confirm the closure"
                  onChange={(e) =>
                    formik.setFieldValue(
                      'confirmPartnerId',
                      e.target.value,
                      true
                    )
                  }
                />
              </Form.Item>
            </Form>
          </FormikProvider>
        </Flex>
      </Modal>
    </>
  );
};
